
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Typography,
} from "@mui/material";

import dayjs from "dayjs";
import { enqueueSnackbar } from "notistack";
import { useRef, useState } from "react";
import { v4 as uuid } from "uuid";

import UploadCloud from "src/components/shared/icon/UploadCloud";
import appConfig from "src/config/env";
import useAppGlobal from "src/context/useAppGlobal";
import useBatchList from "src/hooks/batch/useBatchList";
import useBatchExportItemCodes from "src/hooks/batch/useBatchExportItemCodes";
import useBatchUpdate from "src/hooks/batch/useBatchUpdate";
import useQueryParams from "src/hooks/useQueryParams";
import {
  PERMISSIONS,
  useRoleCheck,
} from "src/hooks/permission/permission";

import ScanItemCodesDialog from "../ScanItemCodesDialog";

export default function useBatchBody({
  warehouseId,
  batchSelect,
  keyword,
  searchBy,
  date,
  prodId,
}) {
  const permissionCheck = useRoleCheck();
  const [option] = useQueryParams();
  const { handleOpenDialog } = useAppGlobal();

  const inputRefs = useRef({});

  const [pagi, setPagi] = useState({
    limit: 50,
    skip: 0,
  });

  const {
    data: batch,
    isLoading,
    error,
    mutate,
  } = useBatchList({
    ...pagi,
    warehouse: warehouseId ? warehouseId : undefined,
    searchField:
      searchBy !== 2
        ? searchBy
          ? batchSelect[searchBy - 1]?.value
          : batchSelect[0]?.value
        : undefined,
    searchValue: keyword ? keyword : undefined,
    product: prodId ? prodId : undefined,
    brand: option?.tab !== "batch" ? option?.tab : undefined,
    ...date,
  });

  const { trigger: updateSerial } = useBatchUpdate();
  const { trigger: exportItemCodes } = useBatchExportItemCodes();

  const [exportingBatchId, setExportingBatchId] = useState(null);
  const [importingBatchId, setImportingBatchId] = useState(null);

  const canManageBatch = permissionCheck([
    PERMISSIONS.CREATE_BATCH_ALL,
    PERMISSIONS.UPDATE_BATCH_ALL,
    PERMISSIONS.CREATE_BATCH_OWN,
    PERMISSIONS.UPDATE_BATCH_OWN,
  ]);

  const handleExportItemCodesList = async (batchId) => {
    if (!batchId || exportingBatchId || importingBatchId) return;

    setExportingBatchId(batchId);

    try {
      const res = await exportItemCodes({
        pathIncluded: { id: batchId },
      });

      if (res?.success && res?.data?.url) {
        window.open(
          `${appConfig.API_FILE}/${res.data.url}`,
          "_blank",
          "noopener,noreferrer"
        );
      } else {
        enqueueSnackbar(
          res?.error?.message || "Không xuất được danh sách số serial.",
          { variant: "error" }
        );
      }
    } catch {
      enqueueSnackbar("Không xuất được danh sách số serial.", {
        variant: "error",
      });
    } finally {
      setExportingBatchId(null);
    }
  };

  const header = [
    {
      id: uuid(),
      title: "STT",
      align: "center",
      width: "20px",
    },
    {
      id: uuid(),
      title: "Lô sản phẩm (LOT)",
      align: "center",
      width: "60px",
    },
    {
      id: uuid(),
      title: "Kho/ NCC",
      align: "center",
      width: "120px",
    },
    {
      id: uuid(),
      title: "Sản phẩm",
      align: "center",
      width: "220px",
    },
    {
      id: uuid(),
      title: "Ngày sản xuất (MFG)",
      width: "60px",
      align: "center",
    },
    {
      id: uuid(),
      title: "Ngày hết hạn (EXP)",
      align: "center",
      width: "60px",
    },
    {
      id: uuid(),
      title: "Số lượng",
      align: "center",
      width: "80px",
    },
    {
      id: uuid(),
      title: "File số serial",
      align: "center",
      width: "180px",
    },
    {
      id: uuid(),
      title: "Thao tác",
      align: "center",
      width: "60px",
    },
  ];

  const handleFileChange = async (event, item) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      enqueueSnackbar("Không có file nào được chọn.", {
        variant: "warning",
      });
      return;
    }

    // Prevent duplicate imports while another import is running.
    if (importingBatchId) {
      event.target.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("warehouse", item?.warehouse?._id);

    setImportingBatchId(item?._id);

    try {
      const res = await updateSerial({
        pathIncluded: { id: item?._id },
        body: formData,
      });

      if (res?.success === true) {
        enqueueSnackbar("Cập nhật số serial thành công!", {
          variant: "success",
        });

        await mutate();
      } else {
        enqueueSnackbar(
          res?.error?.message || "Đã xảy ra lỗi khi cập nhật.",
          { variant: "error" }
        );
      }
    } catch (err) {
      enqueueSnackbar("Lỗi hệ thống. Vui lòng thử lại sau.", {
        variant: "error",
      });
    } finally {
      setImportingBatchId(null);
      event.target.value = "";
    }
  };

  const handleScan = (item) => {
    if (importingBatchId) return;

    handleOpenDialog({
      size: "xl",
      content: (
        <ScanItemCodesDialog
          mutate={mutate}
          item={item}
        />
      ),
    });
  };

  const bodyList =
    Array.isArray(batch?.data) && batch.data.length
      ? batch.data.map((v, index) => {
          const isImporting = importingBatchId === v?._id;
          const isExporting = exportingBatchId === v?._id;

          return {
            ...v,
            no: index + 1,
            provider: v?.warehouse?.name ?? "-",

            createdAt: (
              <Box>
                <Typography sx={{ fontWeight: "bold" }}>
                  {v?.code}
                </Typography>
                <Typography>
                  {dayjs(v?.createdAt).format("DD/MM/YYYY")}
                </Typography>
              </Box>
            ),

            product: (
              <Box display="flex" gap="12px">
                <Box
                  sx={{
                    width: "46px",
                    height: "46px",
                    background: `url(${appConfig.API_FILE}/${v?.productModel?.image?.src}) center / cover no-repeat`,
                    border: "1px solid #cccccc",
                    borderRadius: "8px",
                    backgroundColor: "white",
                  }}
                />

                <Box flex={1} textAlign="left">
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {v?.product?.name} - {v?.productModel?.name}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "12px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {v?.productModel?.code}
                  </Typography>
                </Box>
              </Box>
            ),

            mfgDate: dayjs(v?.mfg).format("DD-MM-YYYY"),
            expDate: dayjs(v?.exp).format("DD-MM-YYYY"),

            file: (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: "12px",
                    justifyContent: "center",
                  }}
                >
                  {!v?.itemCodeFileName ? (
                    <>
                      {v?.totalItemCodes === v?.qty ? (
                        <Typography variant="body2">
                          <CircularProgress size={12} /> Đang xuất file...
                        </Typography>
                      ) : (
                        <>
                          <Box>
                            <input
                              ref={(el) => {
                                if (el) {
                                  inputRefs.current[v._id] = el;
                                }
                              }}
                              type="file"
                              accept=".xls,.xlsx"
                              style={{ display: "none" }}
                              disabled={!canManageBatch || !!importingBatchId}
                              onChange={(e) => handleFileChange(e, v)}
                            />

                            <IconButton
                              disabled={!canManageBatch || !!importingBatchId}
                              sx={{
                                background: "var(--grey)",
                                p: 0,
                                width: "36px",
                                height: "36px",
                              }}
                              onClick={() =>
                                inputRefs.current[v._id]?.click()
                              }
                            >
                              {isImporting ? (
                                <CircularProgress size={20} />
                              ) : (
                                <UploadCloud
                                  color={
                                    permissionCheck([
                                      PERMISSIONS.CREATE_BATCH,
                                      PERMISSIONS.UPDATE_BATCH,
                                    ])
                                      ? "black"
                                      : ""
                                  }
                                />
                              )}
                            </IconButton>
                          </Box>

                          <Button
                            variant="outlined"
                            disabled={!canManageBatch || !!importingBatchId}
                            onClick={() => handleScan(v)}
                            sx={{
                              borderRadius: "8px",
                              border: "1.5px solid",
                            }}
                            startIcon={
                              isImporting ? (
                                <CircularProgress size={18} />
                              ) : (
                                <img
                                  style={{ width: "18px" }}
                                  src="/img/scan-svgrepo-com.svg"
                                  alt=""
                                />
                              )
                            }
                          >
                            {isImporting
                              ? "Đang nhập..."
                              : `${v?.totalItemCodes ?? 0}/${v?.qty}`}
                          </Button>
                        </>
                      )}
                    </>
                  ) : (
                    <Typography
                      component="a"
                      href={`${appConfig.API_FILE}/${v?.itemCodeUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        fontSize: "12px",
                        textDecoration: "underline",
                      }}
                    >
                      {v?.itemCodeFileName ?? "batch.csv"}
                    </Typography>
                  )}
                </Box>

                {!v?.itemCodeFileName &&
                  (v?.totalItemCodes ?? 0) > 0 && (
                    <Link
                      component="button"
                      type="button"
                      disabled={isExporting || !!importingBatchId}
                      onClick={() => handleExportItemCodesList(v._id)}
                      sx={{
                        fontSize: "12px",
                        cursor: isExporting ? "wait" : "pointer",
                        textDecoration: "underline",
                        border: "none",
                        background: "none",
                        p: 0,
                        font: "inherit",
                        color: "primary.main",
                      }}
                    >
                      {isExporting ? "Đang tải…" : "DS số lô"}
                    </Link>
                  )}
              </Box>
            ),

            require: v?.itemCodeFileName
              ? "full"
              : v?.totalItemCodes === 0
                ? ""
                : "delete",
          };
        })
      : null;

  const sortData = [
    "no",
    "createdAt",
    "provider",
    "product",
    "mfgDate",
    "expDate",
    "qty",
    "file",
  ];

  return {
    header,
    bodyList,
    isLoading,
    error,
    setPagi,
    sortData,
    mutate,
    total: batch?.count ?? 0,
    pagi,
  };
}
